import {useEffect, useMemo, useState} from 'react';

import dayjs from 'dayjs';
import {oneOfType, node, func} from 'prop-types';

import {
  API_ENDPOINTS,
  REPORT_DATASOURCES_AUTHENTICATION_METHODS,
  REPORT_DATASOURCES_PRIVACY_LEVELS,
  REPORT_UPDATE_HISTORY_STATUSES,
  REPORT_UPDATES_JOBS_ACTIVE_STATUSES,
  REPORT_UPDATES_JOBS_STATUSES,
  SNACKBAR_ACTIONS
} from '../const';
import ReportParametersContext from '../contexts/ReportParametersContext';
import useHttp from '../hooks/misc/useHttp';
import {usePersistedReducer} from '../hooks/misc/usePersistedReducer';
import useProjects from '../hooks/providers/useProjects';
import useReport from '../hooks/providers/useReport';
import useSnackbar from '../hooks/providers/useSnackbar';
import useWorkspaces from '../hooks/providers/useWorkspaces';
import {ActionCreators, initialState, reducer} from '../reducers/providers/reportParametersReducer';

// eslint-disable-next-line complexity
const ReportParametersProvider = ({children}) => {
  const [reportParametersCategoryExpanded, setReportParametersCategoryExpanded] = useState(null);
  const [gatewaysConnectionsCurrentlyTested, setGatewaysConnectionsCurrentlyTested] = useState([]);
  const [datasetsCurrentlyRefreshing, setDatasetsCurrentlyRefreshing] = useState([]);
  const [refreshesHistory, setRefreshesHistory] = useState({});
  const [isPlannedUpdateModalOpen, setIsPlannedUpdateModalOpen] = useState(false);

  const {_get, _post} = useHttp();

  const {reports, shouldTestReportDatasourceConnections, setShouldTestReportDatasourceConnections, updateGatewayDatasources, addReportRefresh, addReportScheduledUpdate, updateReportDatasources} =
    useReport();
  const {selectedReportTab} = useProjects();
  const {selectedWorkspace} = useWorkspaces();
  const {showSnackbar, closeSnackbar, setSnackbars} = useSnackbar();

  const connectionsReducerKey = 'REPORT_PARAMETERS_REDUCER_KEY';
  const {state, dispatch} = usePersistedReducer(reducer, initialState, connectionsReducerKey);
  const {jobRegistered, jobPolled, jobHandled} = ActionCreators(dispatch);

  const report = reports.find(r => r.report_id === selectedReportTab);
  const reportDataSources = report?.gateway_datasources || [];

  const updateReportJobs = state.updateJobs;
  const activeJobs = Object.values(updateReportJobs).filter(job => !job.handled && REPORT_UPDATES_JOBS_ACTIVE_STATUSES.includes(job.status));

  const addGatewayDatasourcesCurrentlyTesting = datasources => {
    const newSources = datasources.map(s => ({
      gatewayId: s.gateway_id,
      datasourceId: s.id
    }));
    setGatewaysConnectionsCurrentlyTested(current => [...current, ...newSources]);
  };

  const removeGatewayDatasourcesCurrentlyTesting = datasources => {
    setGatewaysConnectionsCurrentlyTested(current => current.filter(g => !datasources.some(s => s.gateway_id === g.gatewayId && s.id === g.datasourceId)));
  };

  const addDatasetCurrentlyRefreshing = datasetId => {
    setDatasetsCurrentlyRefreshing(current => [...current, datasetId]);
  };

  const removeDatasetCurrentlyRefreshing = datasetId => {
    setDatasetsCurrentlyRefreshing(current => current.filter(d => d !== datasetId));
  };

  const addUpdateHistoryEntry = history => {
    const newHistoryEntry = {
      status: history.status,
      refreshId: history.requestId, // At this point we do not know refresh id so we use job requestId instead
      startTime: history.startTime,
      endTime: history.endTime,
      objects: history.objects
    };
    addReportRefresh(report.report_id, newHistoryEntry);
  };

  const isDatasetCurrentlyRefreshing = () => {
    const isRefreshing = datasetsCurrentlyRefreshing.find(d => d === report.dataset_id) !== undefined;
    return isRefreshing;
  };

  const isGatewayDatasourceCurrentlyTested = (datasourceId, gatewayId) => {
    const isTested = gatewaysConnectionsCurrentlyTested.find(g => g.gatewayId === gatewayId && g.datasourceId === datasourceId) !== undefined;
    return isTested;
  };

  const handleJobCompleted = job => {
    setSnackbars(current => current.filter(s => s.action !== SNACKBAR_ACTIONS.UPDATE_REPORT_IN_PROGRESS));

    if (job.status === REPORT_UPDATES_JOBS_STATUSES.COMPLETED) {
      showSnackbar(SNACKBAR_ACTIONS.UPDATE_REPORT_SUCCESS);
    }

    removeDatasetCurrentlyRefreshing(job.dataset_id);

    addUpdateHistoryEntry({
      status: job.status,
      requestId: job.request_id,
      startTime: job.startTime,
      endTime: dayjs().toISOString(),
      objects: job.lastPollData?.objects
    });
  };

  const testGatewayDatasourceConnection = async datasources => {
    try {
      addGatewayDatasourcesCurrentlyTesting(datasources);
      const url = API_ENDPOINTS.reports.parameters.testGatewayConnection;
      const {response, responseJson: data} = await _post(url, {
        datasources_list: datasources
      });
      removeGatewayDatasourcesCurrentlyTesting(datasources);

      if (response.status === 200) {
        updateGatewayDatasources(data);
        return {
          success: true,
          status: 200,
          data
        };
      }

      return {
        success: false,
        status: response.status,
        data
      };
    } catch (e) {
      removeGatewayDatasourcesCurrentlyTesting(datasources);

      // eslint-disable-next-line no-console
      console.error(e);
      return {
        message: e.message
      };
    } finally {
      removeGatewayDatasourcesCurrentlyTesting(datasources);
    }
  };

  const getGateways = async selectedReport => {
    try {
      const url = API_ENDPOINTS.reports.parameters.getGateways;

      const snackbarId = showSnackbar(SNACKBAR_ACTIONS.ACTUALIZE_DATASOURCES, {
        severity: 'warning',
        autoHide: false,
        hasSpinner: true
      });

      const {response, responseJson: data} = await _post(url, {
        workspace_id: selectedReport.workspace_id,
        dataset_id: selectedReport.dataset_id
      });

      if (response.status === 200) {
        const formattedData = data.map(s => ({
          id: s.id,
          gateway_id: s.gatewayId,
          workspace_id: selectedReport.workspace_id,
          dataset_id: selectedReport.dataset_id
        }));

        await testGatewayDatasourceConnection(formattedData);
        closeSnackbar(snackbarId);
        showSnackbar(SNACKBAR_ACTIONS.ACTUALIZE_DATASOURCES_SUCCESS);

        return {
          success: true,
          status: 200,
          data
        };
      }
      closeSnackbar(snackbarId);

      return {
        success: false,
        status: response.status,
        data
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {
        message: e.message
      };
    }
  };

  const runDatasetRefresh = async (tables = null) => {
    try {
      const url = API_ENDPOINTS.reports.parameters.runDatasetRefresh;

      addDatasetCurrentlyRefreshing(report.dataset_id);

      showSnackbar(
        SNACKBAR_ACTIONS.UPDATE_REPORT_IN_PROGRESS,
        {
          severity: 'info',
          autoHide: false
        }
        // {updatedReportName: report.report_name}
      );

      const {response, responseJson: data} = await _post(url, {
        workspace_id: report.workspace_id,
        dataset_id: report.dataset_id,
        tables
      });

      if (response.status === 202) {
        jobRegistered({
          startTime: dayjs().toISOString(),
          report_id: report.report_id,
          request_id: data,
          workspace_id: report.workspace_id,
          dataset_id: report.dataset_id,
          tables
        });
        return {
          success: true,
          status: 202
        };
      }

      setSnackbars(current => current.filter(s => s.action !== SNACKBAR_ACTIONS.UPDATE_REPORT_IN_PROGRESS));
      removeDatasetCurrentlyRefreshing(report.dataset_id);

      if (response.status === 400) {
        showSnackbar(data, {
          severity: 'error',
          duration: 3000
        });
      }

      return {
        success: false,
        status: response.status,
        data
      };
    } catch (e) {
      removeDatasetCurrentlyRefreshing(report.dataset_id);
      // eslint-disable-next-line no-console
      console.error(e);
      return {
        message: e.message
      };
    }
  };

  const cancelDatasetRefresh = async () => {
    try {
      const url = API_ENDPOINTS.reports.parameters.cancelDatasetRefresh;

      // We stop all local update-related state to visually reflect the cancellation
      removeDatasetCurrentlyRefreshing(report.dataset_id);

      const job = activeJobs.find(j => j.report_id === report.report_id);

      const requestId = job?.request_id;
      handleJobCompleted({
        ...job,
        status: REPORT_UPDATE_HISTORY_STATUSES.CANCELLED
      });
      jobHandled(requestId);

      console.log('in cancelDatasetRefresh');

      // Then we call the API to actually cancel
      const {response, responseJson: data} = await _post(url, {
        workspace_id: report.workspace_id,
        dataset_id: report.dataset_id,
        request_id: requestId
      });

      if (response.status === 200) {
        return {
          success: true,
          status: 200
        };
      }

      return {
        success: false,
        status: response.status,
        data
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {
        message: e.message
      };
    }
  };

  const scheduleReportUpdate = async scheduleData => {
    try {
      const url = API_ENDPOINTS.reports.parameters.scheduleUpdate;

      let scheduledData = {
        enabled: scheduleData.enabled
      };

      if (scheduleData.enabled) {
        const selectedDays = Object.keys(scheduleData.weekdays).filter(k => scheduleData.weekdays[k] === true);
        const formattedTimes = scheduleData.executionTimes
          .map(t => t.time)
          .filter(Boolean)
          .map(time => dayjs(time).format('HH:mm'));

        scheduledData = {
          ...scheduledData,
          days: selectedDays,
          times: formattedTimes,
          localTimeZoneId: scheduleData.localTimeZoneId
        };
      }

      const snackbarId = showSnackbar(SNACKBAR_ACTIONS.SCHEDULE_REPORT_UPDATE_IN_PROGRESS, {
        severity: 'warning',
        autoHide: false,
        hasSpinner: true
      });

      const {response, responseJson: data} = await _post(url, {
        workspace_id: report.workspace_id,
        dataset_id: report.dataset_id,
        schedule_data: scheduledData
      });

      closeSnackbar(snackbarId);

      if (response.status === 200) {
        addReportScheduledUpdate(report.report_id, scheduledData);
        showSnackbar(SNACKBAR_ACTIONS.SCHEDULE_REPORT_UPDATE_SUCCESS);
        return {
          success: true,
          status: 200
        };
      }
      showSnackbar(data, {
        severity: 'error',
        duration: 4000
      });

      return {
        success: false,
        status: response.status,
        data
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {
        message: e.message
      };
    }
  };

  const getDatasetRefreshesHistory = async () => {
    try {
      const url = API_ENDPOINTS.reports.parameters.getRefreshHistory;

      const {response, responseJson: data} = await _post(url, {
        workspace_id: report.workspace_id,
        dataset_id: report.dataset_id
      });

      if (response.status === 200) {
        setRefreshesHistory(current => {
          return {
            ...current,
            [report.report_id]: data
          };
        });
        return {
          success: true,
          status: 200,
          data
        };
      }
      return {
        success: false,
        status: response.status,
        data
      };
    } catch (e) {
      removeDatasetCurrentlyRefreshing(report.dataset_id);
      // eslint-disable-next-line no-console
      console.error(e);
      return {
        message: e.message
      };
    }
  };

  const getCredentialsFromFormData = formData => {
    switch (formData.authenticationMethod) {
      case REPORT_DATASOURCES_AUTHENTICATION_METHODS.ANONYMOUS.id:
        return null;
      case REPORT_DATASOURCES_AUTHENTICATION_METHODS.BASIC.id:
        return {
          username: formData.username,
          password: formData.password
        };
      case REPORT_DATASOURCES_AUTHENTICATION_METHODS.WINDOWS.id:
        return {
          username: formData.username,
          password: formData.password
        };
      case REPORT_DATASOURCES_AUTHENTICATION_METHODS.OAUTH.id:
        return {
          access_token: formData.access_token
        };

      case REPORT_DATASOURCES_AUTHENTICATION_METHODS.KEY.id:
        return {
          key: formData.key
        };
      case REPORT_DATASOURCES_AUTHENTICATION_METHODS.SAS.id:
        return {
          token: formData.token
        };
      default:
        return null;
    }
  };

  const updateGatewayDatasourceConnection = async (datasourceId, gatewayId, formData) => {
    try {
      const credentialsConnection = getCredentialsFromFormData(formData);
      const credentialsDetails = {
        credentials_connection: credentialsConnection,
        credential_type: formData.authenticationMethod || REPORT_DATASOURCES_AUTHENTICATION_METHODS.ANONYMOUS.id,
        privacy_level: formData.privacyLevel || REPORT_DATASOURCES_PRIVACY_LEVELS.NONE.id
      };

      const url = API_ENDPOINTS.reports.parameters.updateGatewayConnection;

      const snackbarId = showSnackbar(SNACKBAR_ACTIONS.UPDATE_SOURCE_IN_PROGRESS, {
        severity: 'warning',
        autoHide: false,
        hasSpinner: true
      });

      const {response, responseJson: data} = await _post(url, {
        workspace_id: report.workspace_id,
        dataset_id: report.dataset_id,
        id: datasourceId,
        gateway_id: gatewayId,
        ignore_test_connection: formData.shouldIgnoreConnectionTest,
        credentials_details: credentialsDetails
      });

      if (response.status === 200) {
        const sourceData = {
          id: datasourceId,
          gateway_id: gatewayId,
          workspace_id: report.workspace_id,
          dataset_id: report.dataset_id
        };
        const datasources = [sourceData];
        testGatewayDatasourceConnection(datasources);
        closeSnackbar(snackbarId);

        return {
          success: true,
          status: 200,
          data
        };
      }

      closeSnackbar(snackbarId);
      return {
        success: false,
        status: response.status,
        data
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {
        message: e.message
      };
    }
  };

  const updateDatasetParameters = async newParameters => {
    try {
      const url = API_ENDPOINTS.reports.parameters.updateParameters;

      const snackbarId = showSnackbar(SNACKBAR_ACTIONS.UPDATE_PARAMETERS_IN_PROGRESS, {
        severity: 'warning',
        autoHide: false,
        hasSpinner: true
      });

      // eslint-disable-next-line no-unreachable
      const {response, responseJson: data} = await _post(url, {
        workspace_id: report.workspace_id,
        dataset_id: report.dataset_id,
        workspace_name: selectedWorkspace.workspace_name,
        report_name: report.report_name,
        parameters: newParameters
      });

      closeSnackbar(snackbarId);

      if (response.status === 200) {
        showSnackbar(SNACKBAR_ACTIONS.UPDATE_PARAMETERS_SUCCESS);
        return {
          success: true,
          status: 200,
          data
        };
      }

      if (response.status === 400) {
        showSnackbar(data, {severity: 'error', duration: 4000});
      }

      return {
        success: false,
        status: response.status,
        data
      };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {
        message: e.message
      };
    }
  };

  const pollUpdateReportJob = async job => {
    try {
      const url = API_ENDPOINTS.reports.parameters.checkDatasetRefresh;

      const {responseJson: data} = await _post(
        url,
        {
          workspace_id: job.workspace_id,
          dataset_id: job.dataset_id,
          request_id: job.request_id
        },
        {shouldDisplayErrorSnackbar: false}
      );

      jobPolled({
        ...data,
        workspace_id: job.workspace_id,
        dataset_id: job.dataset_id,
        request_id: job.request_id
      });
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    const completedUnhandledJobs = Object.values(updateReportJobs).filter(
      job => (job.status === REPORT_UPDATES_JOBS_STATUSES.COMPLETED || job.status === REPORT_UPDATES_JOBS_STATUSES.FAILED) && !job.handled
    );

    const handleAllJobs = async () => {
      // eslint-disable-next-line no-restricted-syntax
      for (const job of completedUnhandledJobs) {
        // eslint-disable-next-line no-await-in-loop
        handleJobCompleted(job);
        jobHandled(job.request_id);
      }
    };

    handleAllJobs();
  }, [updateReportJobs]);

  useEffect(() => {
    if (activeJobs.length === 0) {
      return;
    }

    // --- keep only one progress snackbar --- // TODO This might change ? Ask adrian
    setSnackbars(current => current.filter(s => s.action !== SNACKBAR_ACTIONS.UPDATE_REPORT_IN_PROGRESS));

    showSnackbar(SNACKBAR_ACTIONS.UPDATE_REPORT_IN_PROGRESS, {
      severity: 'info',
      autoHide: false
    });
  }, [updateReportJobs]);

  useEffect(() => {
    if (report?.report_id) {
      setReportParametersCategoryExpanded(null);
    }
  }, [report?.report_id]);

  useEffect(() => {
    (async () => {
      if (!shouldTestReportDatasourceConnections || !reports?.length) {
        return;
      }

      const allSources = reports.flatMap(r => r.gateway_datasources.map(s => ({...s, dataset_id: r.dataset_id, workspace_id: r.workspace_id})));
      const uniqueSources = Array.from(new Map(allSources.map(s => [s.id, s])).values());
      const formattedSources = uniqueSources.map(source => ({
        id: source.id,
        gateway_id: source.gatewayId,
        workspace_id: source.workspace_id,
        dataset_id: source.dataset_id
      }));
      console.log({allSources, uniqueSources, formattedSources});

      await testGatewayDatasourceConnection(formattedSources);
      setShouldTestReportDatasourceConnections(false);
    })();
  }, [shouldTestReportDatasourceConnections, reports]);

  const currentReportJobUpdate = Object.values(updateReportJobs).find(j => j.report_id === report?.report_id);
  const shortRefreshesHistory = report?.refreshes || [];
  const fullRefreshesHistory = report ? refreshesHistory[report.report_id] : [];
  const reportTables = report ? report.tables : [];
  const hasPlannedUpdate = report?.refreshSchedule?.enabled ?? false;
  const areAllSourcesConnected = reportDataSources.length > 0 && reportDataSources.every(s => s.is_connected?.code === 200);

  const useMemoDeps = [
    updateGatewayDatasourceConnection,
    isGatewayDatasourceCurrentlyTested,
    reportParametersCategoryExpanded,
    setReportParametersCategoryExpanded,
    reportDataSources,
    testGatewayDatasourceConnection,
    runDatasetRefresh,
    cancelDatasetRefresh,
    isDatasetCurrentlyRefreshing,
    getDatasetRefreshesHistory,
    refreshesHistory,
    updateReportJobs,
    pollUpdateReportJob,
    currentReportJobUpdate,
    shortRefreshesHistory,
    fullRefreshesHistory,
    reportTables,
    scheduleReportUpdate,
    hasPlannedUpdate,
    report,
    isPlannedUpdateModalOpen,
    setIsPlannedUpdateModalOpen,
    updateDatasetParameters,
    areAllSourcesConnected,
    getGateways
  ];

  const value = useMemo(
    () => ({
      updateGatewayDatasourceConnection,
      isGatewayDatasourceCurrentlyTested,
      testGatewayDatasourceConnection,
      reportParametersCategoryExpanded,
      setReportParametersCategoryExpanded,
      reportDataSources,
      runDatasetRefresh,
      cancelDatasetRefresh,
      isDatasetCurrentlyRefreshing,
      getDatasetRefreshesHistory,
      refreshesHistory,
      updateReportJobs,
      pollUpdateReportJob,
      currentReportJobUpdate,
      shortRefreshesHistory,
      fullRefreshesHistory,
      reportTables,
      scheduleReportUpdate,
      hasPlannedUpdate,
      currentReport: report,
      isPlannedUpdateModalOpen,
      setIsPlannedUpdateModalOpen,
      updateDatasetParameters,
      areAllSourcesConnected,
      getGateways
    }),
    useMemoDeps
  );

  return <ReportParametersContext.Provider value={value}>{children}</ReportParametersContext.Provider>;
};

ReportParametersProvider.propTypes = {
  children: oneOfType([node, func]).isRequired
};

export default ReportParametersProvider;
